/**
 * What a finding's form is: its shape, and the inputs that fill it.
 *
 * Split from `findingEditor.tsx`, which is how a finding is *loaded and saved*. The seam is
 * that nothing in this file touches the network or holds state — every component here takes
 * the current values and a way to patch them, so the form can be read without reading the
 * fetch, and the fetch without reading forty labels.
 *
 * The one thing they must agree on is `EditorState`, and it lives here because it describes
 * the fields rather than the request: every value is a string because that is what an input
 * holds, and `toPayload` is where they become what the server takes.
 */

import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import type { FindingFields } from '../account'
import { listFindings, listThemes } from '../account'
import { t } from '../i18n'

import { toList } from './recordEditing'
import { CLAIM_TYPES, ORIGINS, STATUSES } from './vocabulary'

export interface EditorState {
  title: string
  status: string
  claim_type: string
  origin: string
  slug: string
  themes: string
  keywords: string
  scripture: string
  sources: string
  related: string
  builds_on: string
  tension_with: string
  body: string
}

export const BLANK: EditorState = {
  title: '',
  status: 'hypothesis',
  claim_type: '',
  origin: 'own',
  slug: '',
  themes: '',
  keywords: '',
  scripture: '',
  sources: '',
  related: '',
  builds_on: '',
  tension_with: '',
  body: '',
}

/**
 * The form's strings, as the fields the server takes.
 *
 * Every list arrives from the form as one comma-separated string, because that is what a
 * text input holds; `toList` is where it becomes a list again. The two `|| null` cases are
 * the picker's "not chosen" option, which is an empty string in HTML and has to reach the
 * server as an absent value rather than as a claim_type of nothing.
 */
export function toPayload(fields: EditorState): FindingFields {
  return {
    title: fields.title,
    status: fields.status as never,
    claim_type: (fields.claim_type || null) as never,
    origin: (fields.origin || null) as never,
    themes: toList(fields.themes),
    keywords: toList(fields.keywords),
    scripture: toList(fields.scripture),
    sources: toList(fields.sources),
    related: toList(fields.related),
    builds_on: toList(fields.builds_on),
    tension_with: toList(fields.tension_with),
    body: fields.body,
  }
}

/** How a form patches itself: the current values in, one field's change out. */
type SetField = (patch: Partial<EditorState>) => void

/** How a finding is classified: its maturity, its epistemic kind, and whose reading it is. */
export function ClassificationRow({
  fields,
  set,
}: {
  fields: EditorState
  set: SetField
}): JSX.Element {
  return (
    <div className="form__row">
      <label>
        {t('editor.status')}
        <select value={fields.status} onChange={(event) => set({ status: event.target.value })}>
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`findings.status.${status}`)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t('editor.kind')}
        <select
          value={fields.claim_type}
          onChange={(event) => set({ claim_type: event.target.value })}
        >
          <option value="">{t('editor.unset')}</option>
          {CLAIM_TYPES.map((kind) => (
            <option key={kind} value={kind}>
              {t(`claim.type.${kind}`)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t('editor.origin')}
        <select value={fields.origin} onChange={(event) => set({ origin: event.target.value })}>
          <option value="">{t('editor.unset')}</option>
          {/* `own` is what the reader picks; the server stores their name,
              because "own" stops being true the moment this travels. */}
          {ORIGINS.map((origin) => (
            <option key={origin} value={origin}>
              {t(`origin.${origin}`)}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}

/** What the finding is about: themes, keywords, the scripture it rests on, its sources. */
export function DescriptorFields({
  fields,
  set,
}: {
  fields: EditorState
  set: SetField
}): JSX.Element {
  return (
    <>
      <ListField
        label={t('editor.themes')}
        value={fields.themes}
        onChange={(themes) => set({ themes })}
        options="workspace-themes"
        hint={t('editor.themesHint')}
      />
      <ListField
        label={t('editor.keywords')}
        value={fields.keywords}
        onChange={(keywords) => set({ keywords })}
      />
      <ListField
        label={t('editor.scripture')}
        value={fields.scripture}
        onChange={(scripture) => set({ scripture })}
        hint={t('editor.scriptureHint')}
      />
      <ListField
        label={t('editor.sources')}
        value={fields.sources}
        onChange={(sources) => set({ sources })}
      />
    </>
  )
}

/**
 * The typed edges into the rest of the corpus.
 *
 * Their own group because the graph is the point: these three are what turn a pile of
 * claims into something usable later, and they all offer the same completion list.
 */
export function EdgeFields({
  fields,
  set,
}: {
  fields: EditorState
  set: SetField
}): JSX.Element {
  return (
    <>
      <ListField
        label={t('edge.builds_on')}
        value={fields.builds_on}
        onChange={(builds_on) => set({ builds_on })}
        options="workspace-findings"
      />
      <ListField
        label={t('edge.tension_with')}
        value={fields.tension_with}
        onChange={(tension_with) => set({ tension_with })}
        options="workspace-findings"
      />
      <ListField
        label={t('edge.related')}
        value={fields.related}
        onChange={(related) => set({ related })}
        options="workspace-findings"
      />
    </>
  )
}

/**
 * The finding's id, and the warning that changing it breaks other people's corpora.
 *
 * The warning is the reason this is a component rather than one more `<label>`: it depends
 * on whether the finding has been published, which nothing else in the form cares about.
 */
export function SlugField({
  slug,
  originalSlug,
  published,
  set,
}: {
  slug: string
  originalSlug: string
  published: boolean
  set: SetField
}): JSX.Element {
  return (
    <label>
      {t('editor.slug')}
      <input value={slug} onChange={(event) => set({ slug: event.target.value })} />
      {published && slug !== originalSlug && (
        <span className="failure" role="alert">
          {t('editor.slugPublished')}
        </span>
      )}
      {!published && <span className="muted">{t('editor.slugHint')}</span>}
    </label>
  )
}

export function ListField({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options?: string
  hint?: string
}): JSX.Element {
  return (
    <label>
      {label}
      <input value={value} onChange={(event) => onChange(event.target.value)} list={options} />
      <span className="muted">{hint ?? t('editor.commaSeparated')}</span>
    </label>
  )
}

/**
 * The ids an editor can offer: every theme, and every finding including
 * imported ones.
 *
 * Imports belong in the list precisely because they are read-only: building on
 * somebody else's finding is what PACKS.md tells a reader to do with one, so
 * an edge picker that hid them would hide the documented use.
 */
export function SlugOptions({ corpus }: { corpus: string | null }): JSX.Element {
  const [themes, setThemes] = useState<string[]>([])
  const [findings, setFindings] = useState<string[]>([])

  useEffect(() => {
    listThemes(corpus ?? undefined).then(
      (rows) => setThemes(rows.map((theme) => theme.slug)),
      () => setThemes([]),
    )
    listFindings({ corpus: corpus ?? undefined, limit: 200, sort: 'title', direction: 'asc' }).then(
      (page) => setFindings(page.findings.map((finding) => finding.slug)),
      () => setFindings([]),
    )
  }, [corpus])

  return (
    <>
      <datalist id="workspace-themes">
        {themes.map((slug) => (
          <option key={slug} value={slug} />
        ))}
      </datalist>
      <datalist id="workspace-findings">
        {findings.map((slug) => (
          <option key={slug} value={slug} />
        ))}
      </datalist>
    </>
  )
}
